package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.border
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ReceiptLong
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.Dashboard
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.NavigationRailItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.runtime.Composable
import androidx.compose.ui.ExperimentalComposeUiApi
import kotlin.OptIn
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.runtime.remember
import androidx.compose.ui.tooling.preview.PreviewScreenSizes
import androidx.compose.ui.unit.dp
import androidx.window.core.layout.WindowSizeClass.Companion.WIDTH_DP_MEDIUM_LOWER_BOUND
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.koin.compose.viewmodel.koinViewModel
import com.daytimegaming.ourdailyfinances.presentation.preview.HomeScreenPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.preview.HomeScreenPreviewState
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme

enum class HomeTab {
    Dashboards,
    Accounts,
    Transactions,
    Settings
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun HomeScreen(
    onDashboardClick: (String) -> Unit,
    onAccountClick: (String) -> Unit,
    onRealEstateHoldingClick: (String) -> Unit,
    onPlaidTokenReady: (String) -> Unit,
    onManageAccounts: () -> Unit,
    onAddRealEstateHolding: () -> Unit,
    onCreateDashboard: () -> Unit,
    onJoinDashboard: () -> Unit,
    onViewRecentSpending: () -> Unit,
    onViewRecurringTransactions: () -> Unit,
    onViewIncomeVsExpenses: () -> Unit = {},
    onCreateBudget: () -> Unit,
    onBudgetClick: (String) -> Unit,
    onTransactionClick: (String) -> Unit,
    onManageSubscription: () -> Unit = {},
    modifier: Modifier = Modifier,
    initialTabName: String? = null,
    onTabChanged: ((String) -> Unit)? = null,
    dashboardsViewModel: DashboardsViewModel = koinViewModel(),
    accountsViewModel: AccountsViewModel = koinViewModel(),
) {
    // Backstack implementation using enum names to ensure safe bundle serialization
    var tabBackStackNames by rememberSaveable { mutableStateOf(listOf(HomeTab.Dashboards.name)) }
    val tabBackStack = tabBackStackNames.map { HomeTab.valueOf(it) }
    
    val selectedTab = if (onTabChanged != null) {
        remember(initialTabName) {
            when (initialTabName?.lowercase()) {
                "dashboards" -> HomeTab.Dashboards
                "accounts" -> HomeTab.Accounts
                "transactions" -> HomeTab.Transactions
                "settings" -> HomeTab.Settings
                else -> HomeTab.Dashboards
            }
        }
    } else {
        tabBackStack.lastOrNull() ?: HomeTab.Dashboards
    }

    val selectTab: (HomeTab) -> Unit = { tab ->
        if (onTabChanged != null) {
            onTabChanged(tab.name.lowercase())
        } else {
            if (tabBackStackNames.lastOrNull() != tab.name) {
                tabBackStackNames = tabBackStackNames + tab.name
            }
        }
    }

    val navState = rememberNavigationEventState(NavigationEventInfo.None)

    // Intercept back presses to navigate to the previously selected navigation item
    if (onTabChanged == null) {
        NavigationBackHandler(
            state = navState,
            isBackEnabled = tabBackStackNames.size > 1,
            onBackCompleted = {
                if (tabBackStackNames.size > 1) {
                    tabBackStackNames = tabBackStackNames.dropLast(1)
                }
            }
        )
    }

    val dashboardsState by dashboardsViewModel.state.collectAsStateWithLifecycle()
    val accountsState by accountsViewModel.state.collectAsStateWithLifecycle()

    val showWarningBadge = remember(dashboardsState, accountsState) {
        val userAccountsAttention = (accountsState as? AccountsScreenState.Loaded)
            ?.accounts?.any { it.loginRequired || it.updateRequired } == true

        val dashboardAccountsAttention = (dashboardsState as? DashboardsScreenState.Loaded)
            ?.dashboards?.any { d -> d.accounts.any { it.loginRequired || it.updateRequired } } == true

        userAccountsAttention || dashboardAccountsAttention
    }

    var showAddAccountBottomSheetOnAccounts by rememberSaveable { mutableStateOf(false) }

    HomeScreenContent(
        selectedTab = selectedTab,
        onTabSelect = selectTab,
        showWarningBadge = showWarningBadge,
        modifier = modifier,
        dashboardsScreen = {
            DashboardsScreen(
                onDashboardClick = onDashboardClick,
                onCreateDashboardClick = onCreateDashboard,
                onJoinDashboardClick = onJoinDashboard,
                onViewRecentSpendingClick = onViewRecentSpending,
                onViewRecurringTransactionsClick = onViewRecurringTransactions,
                onViewIncomeVsExpensesClick = onViewIncomeVsExpenses,
                onCreateBudgetClick = onCreateBudget,
                onBudgetClick = onBudgetClick,
                onManageAccounts = onManageAccounts,
                onLinkFirstAccountClick = {
                    selectTab(HomeTab.Accounts)
                    showAddAccountBottomSheetOnAccounts = true
                }
            )
        },
        accountsScreen = {
            AccountsScreen(
                onAccountClick = onAccountClick,
                onRealEstateHoldingClick = onRealEstateHoldingClick,
                onPlaidTokenReady = onPlaidTokenReady,
                onManageAccounts = onManageAccounts,
                onAddRealEstateHoldingClick = onAddRealEstateHolding,
                showAddAccountBottomSheet = showAddAccountBottomSheetOnAccounts,
                onAddAccountBottomSheetDismissed = { showAddAccountBottomSheetOnAccounts = false }
            )
        },
        transactionsScreen = {
            TransactionsScreen(onTransactionClick = onTransactionClick)
        },
        settingsScreen = {
            SettingsScreen(onManageSubscriptionClick = onManageSubscription)
        }
    )
}

@Composable
fun HomeScreenContent(
    selectedTab: HomeTab,
    onTabSelect: (HomeTab) -> Unit,
    dashboardsScreen: @Composable () -> Unit,
    accountsScreen: @Composable () -> Unit,
    transactionsScreen: @Composable () -> Unit,
    settingsScreen: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    showWarningBadge: Boolean = false,
) {
    val adaptiveInfo = currentWindowAdaptiveInfo()
    val isPhonePortrait = !adaptiveInfo.windowSizeClass.isWidthAtLeastBreakpoint(WIDTH_DP_MEDIUM_LOWER_BOUND)

    Scaffold(
        bottomBar = {
            if (isPhonePortrait) {
                GlassBottomBar(
                    selectedTab = selectedTab,
                    onTabSelect = onTabSelect,
                    showWarningBadge = showWarningBadge
                )
            }
        },
        modifier = modifier
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .consumeWindowInsets(innerPadding)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
            ) {
                if (!isPhonePortrait) {
                    GlassNavigationRail(
                        selectedTab = selectedTab,
                        onTabSelect = onTabSelect,
                        showWarningBadge = showWarningBadge
                    )
                }

                Box(
                    modifier = Modifier.fillMaxSize()
                ) {
                    when (selectedTab) {
                        HomeTab.Dashboards -> dashboardsScreen()
                        HomeTab.Accounts -> accountsScreen()
                        HomeTab.Transactions -> transactionsScreen()
                        HomeTab.Settings -> settingsScreen()
                    }
                }
            }
        }
    }
}

@Composable
private fun GlassBottomBar(
    selectedTab: HomeTab,
    onTabSelect: (HomeTab) -> Unit,
    showWarningBadge: Boolean = false,
) {
    val tabs = listOf(
        HomeTab.Dashboards to Icons.Default.Dashboard,
        HomeTab.Accounts to Icons.Default.AccountBalance,
        HomeTab.Transactions to Icons.AutoMirrored.Filled.ReceiptLong,
        HomeTab.Settings to Icons.Default.Settings
    )

    NavigationBar(
        containerColor = Color(0x221D2022), // semi-transparent surface-container
        tonalElevation = 0.dp,
        modifier = Modifier
            .border(width = 1.dp, color = Color(0x1AFFFFFF), shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp))
    ) {
        tabs.forEach { (tab, icon) ->
            NavigationBarItem(
                selected = selectedTab == tab,
                onClick = { onTabSelect(tab) },
                icon = {
                    val iconComposable = @Composable {
                        Icon(
                            imageVector = icon,
                            contentDescription = tab.name,
                            tint = if (selectedTab == tab) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    if (tab == HomeTab.Accounts && showWarningBadge) {
                        BadgedBox(
                            badge = {
                                Badge(containerColor = MaterialTheme.colorScheme.error) {
                                    Text(text = "!")
                                }
                            }
                        ) {
                            iconComposable()
                        }
                    } else {
                        iconComposable()
                    }
                },
                label = {
                    Text(
                        text = tab.name,
                        style = MaterialTheme.typography.labelSmall,
                        color = if (selectedTab == tab) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                colors = NavigationBarItemDefaults.colors(
                    indicatorColor = Color(0x1F57F1DB) // Low opacity teal glow selection indicator
                )
            )
        }
    }
}

@Composable
private fun GlassNavigationRail(
    selectedTab: HomeTab,
    onTabSelect: (HomeTab) -> Unit,
    showWarningBadge: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val tabs = listOf(
        HomeTab.Dashboards to Icons.Default.Dashboard,
        HomeTab.Accounts to Icons.Default.AccountBalance,
        HomeTab.Transactions to Icons.AutoMirrored.Filled.ReceiptLong,
        HomeTab.Settings to Icons.Default.Settings
    )

    NavigationRail(
        containerColor = Color(0x221D2022), // semi-transparent surface-container
        modifier = modifier
            .fillMaxHeight()
            .border(width = 1.dp, color = Color(0x1AFFFFFF), shape = RoundedCornerShape(topEnd = 24.dp, bottomEnd = 24.dp))
    ) {
        tabs.forEach { (tab, icon) ->
            NavigationRailItem(
                modifier = Modifier.padding(4.dp),
                selected = selectedTab == tab,
                onClick = { onTabSelect(tab) },
                icon = {
                    val iconComposable = @Composable {
                        Icon(
                            imageVector = icon,
                            contentDescription = tab.name,
                            tint = if (selectedTab == tab) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    if (tab == HomeTab.Accounts && showWarningBadge) {
                        BadgedBox(
                            badge = {
                                Badge(containerColor = MaterialTheme.colorScheme.error) {
                                    Text(text = "!")
                                }
                            }
                        ) {
                            iconComposable()
                        }
                    } else {
                        iconComposable()
                    }
                },
                label = {
                    Text(
                        text = tab.name,
                        style = MaterialTheme.typography.labelSmall,
                        color = if (selectedTab == tab) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                colors = NavigationRailItemDefaults.colors(
                    indicatorColor = Color(0x1F57F1DB) // Low opacity teal glow selection indicator
                )
            )
        }
    }
}

@PreviewLightDark
@PreviewScreenSizes
@Composable
fun HomeScreenPreview(
    @PreviewParameter(HomeScreenPreviewParameterProvider::class) state: HomeScreenPreviewState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            HomeScreenContent(
                selectedTab = state.selectedTab,
                onTabSelect = {},
                showWarningBadge = state.showWarningBadge,
                dashboardsScreen = {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "Dashboards Screen Preview",
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                accountsScreen = {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "Accounts Screen Preview",
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                transactionsScreen = {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "Transactions Screen Preview",
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                settingsScreen = {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "Settings Screen Preview",
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            )
        }
    }
}

